import React from 'react';

// import { AppsFilterGlyph } from '../../../constants/icons/app-filter';
// import { FolderOpenGlyph } from '../../../constants/icons/folder-open';
// import { LabelGlyph } from '../../../constants/icons/label';
// import { PersonGlyph } from '../../../constants/icons/person';

import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import JobFamilyIcon from '@atlaskit/icon/core/briefcase';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import FileIcon from '@atlaskit/icon/core/files';
import FilterIcon from '@atlaskit/icon/core/filter';
import FocusAreaIcon from '@atlaskit/icon/core/focus-area';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import HashtagIcon from '@atlaskit/icon/core/hashtag';
import BulletedListIcon from '@atlaskit/icon/core/list-bulleted';
import PersonIcon from '@atlaskit/icon/core/person';
import PersonAddedIcon from '@atlaskit/icon/core/person-added';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import SortDescendingIcon from '@atlaskit/icon/core/sort-descending';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import TagIcon from '@atlaskit/icon/core/tag';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { type FilterOrControlId } from '../../../constants/filters/types';
import { serveConfluenceSpaceIconUpdate } from '../../../utils/experiments';

export const getIconColor: (
	isSelected?: boolean,
) => 'var(--ds-icon-selected)' | 'var(--ds-icon-subtle)' = (isSelected?: boolean) => {
	if (isSelected) {
		return token('color.icon.selected');
	}

	return token('color.icon.subtle');
};

export const getIcon: ({
	filterOrControlId,
	isSelected,
	label,
}: {
	filterOrControlId: FilterOrControlId;
	isSelected: boolean;
	label?: string;
}) => React.JSX.Element | null | undefined = ({
	filterOrControlId,
	isSelected,
	label = '',
}: {
	filterOrControlId: FilterOrControlId;
	isSelected: boolean;
	label?: string;
}): React.JSX.Element | null | undefined => {
	const iconProps = {
		label,
		color: getIconColor(isSelected),
	};

	switch (filterOrControlId) {
		case 'owner':
		case 'assignee':
		case 'reporter':
		case 'contributor':
		case 'createdBy':
			return <PersonIcon spacing="none" {...iconProps} />;
		case 'verified':
			return <StatusVerifiedIcon {...iconProps} />;
		case 'label':
			return <TagIcon spacing="none" {...iconProps} />;
		case 'content':
			return <BulletedListIcon spacing="none" {...iconProps} />;
		case 'focusAreaType':
		case 'focusAreaUnder':
		case 'positionFocusArea':
			return <FocusAreaIcon spacing="none" {...iconProps} />;
		case 'lastUpdated':
		case 'closeDate':
			return <CalendarIcon spacing="none" {...iconProps} />;
		case 'project':
			return <SpacesIcon spacing="none" {...iconProps} />;
		case 'space':
			return serveConfluenceSpaceIconUpdate() ? (
				<SpacesIcon spacing="none" {...iconProps} />
			) : (
				<FolderOpenIcon spacing="none" {...iconProps} />
			);
		case 'onlyTitleMatch':
			return null; // Title match doesn't show an icon, only a checkbox
		case 'statusFilter':
		case 'pageStatus':
			return <CheckCircleIcon spacing="none" {...iconProps} />;
		case 'lastUpdatedBy':
			return <PersonIcon spacing="none" {...iconProps} />;
		case 'universalTypeFilter':
			return <FileIcon spacing="none" {...iconProps} />;
		case 'dropdown':
			return fg('platform_search_filter_button_icon_refresh') ? (
				<FilterIcon spacing="none" {...iconProps} />
			) : (
				<CustomizeIcon spacing="none" {...iconProps} />
			);
		case 'sort':
			return <SortDescendingIcon spacing="none" {...iconProps} />;
		case 'assetsSchema':
			return <AssetsSchemaIcon spacing="none" {...iconProps} />;
		case 'positionTeam':
			return <TeamsIcon spacing="none" {...iconProps} />;
		case 'positionEmploymentType':
		case 'positionStatus':
			return <PersonAddedIcon spacing="none" {...iconProps} />;
		case 'pullRequestStatus':
			return <PullRequestIcon spacing="none" {...iconProps} />;
		case 'slackChannel':
			return <HashtagIcon spacing="none" {...iconProps} />;
		case 'positionJobFamily':
			return <JobFamilyIcon spacing="none" {...iconProps} />;
	}
};

export const FilterButtonIconContainer: ({
	children,
}: React.PropsWithChildren) => React.JSX.Element = ({
	children,
}: React.PropsWithChildren): React.JSX.Element => {
	return <Box xcss={filterButtonIconContainerStyles}>{children}</Box>;
};

const filterButtonIconContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.075',
});
